// src/components/ErrorBoundary.js
// Error Boundary component that integrates with logging service
import React from 'react';
import loggingService from '../services/loggingService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to logging service
    this.logError(error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });
  }

  async logError(error, errorInfo) {
    try {
      await loggingService.logError(
        'react_error_boundary',
        'Uncaught error in React component',
        error,
        {
          componentStack: errorInfo?.componentStack || null,
          errorBoundary: true,
          errorName: error?.name || 'Unknown',
          errorMessage: error?.message || 'Unknown error',
        }
      );
    } catch (logError) {
      // If logging fails, at least log to console
      console.error('Error logging to logging service:', logError);
    }
  }

  render() {
    if (this.state.hasError) {
      // You can customize this error UI
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-700 mb-4">
              We're sorry, but something unexpected happened. The error has been
              logged and we'll look into it.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-600 mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs bg-gray-100 p-4 rounded overflow-auto max-h-64">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

